/**
 * 독서 기록(records) 공통 유틸.
 *
 * 기록은 두 종류다.
 * - quote   인용: 책에서 옮겨 적은 문장
 * - thought 생각: 내가 떠올린 생각. parentId 가 있으면 특정 인용에 달린 생각이다.
 */

export const RECORD_TYPES = {
  QUOTE: 'quote',
  THOUGHT: 'thought'
};

export const RECORD_TYPE_LABEL = {
  quote: '인용',
  thought: '생각'
};

/** Firestore Timestamp | Date | number → Date (없으면 null) */
export const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value.toDate === 'function') return value.toDate();
  if (typeof value === 'number') return new Date(value);
  if (typeof value === 'string') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  if (typeof value.seconds === 'number') return new Date(value.seconds * 1000);
  return null;
};

/** 정렬용 밀리초. createdAt 이 아직 서버에서 안 돌아왔으면 date 를 폴백으로 쓴다. */
export const recordTimeMs = (record) => {
  const created = toDate(record.createdAt);
  if (created) return created.getTime();
  if (record.date) return new Date(`${record.date}T12:00:00+09:00`).getTime();
  return 0;
};

/** 오후 4:46 형태 */
export const formatTime = (value) => {
  const date = toDate(value);
  if (!date) return '';
  return date.toLocaleTimeString('ko-KR', {
    hour: 'numeric',
    minute: '2-digit'
  });
};

/** 2026년 8월 3일 */
export const formatLongDate = (dateStr) => {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-').map(Number);
  if (!y || !m || !d) return dateStr;
  return `${y}년 ${m}월 ${d}일`;
};

/** 오늘 / 어제 / 8월 1일 (금) */
export const formatDateGroupLabel = (dateStr, today) => {
  if (dateStr === today) return '오늘';

  const [y, m, d] = dateStr.split('-').map(Number);
  if (!y || !m || !d) return dateStr;

  const target = new Date(Date.UTC(y, m - 1, d));
  const [ty, tm, td] = today.split('-').map(Number);
  const todayUtc = new Date(Date.UTC(ty, tm - 1, td));
  const diffDays = Math.round((todayUtc - target) / 86400000);

  if (diffDays === 1) return '어제';

  const weekday = ['일', '월', '화', '수', '목', '금', '토'][target.getUTCDay()];
  return `${m}월 ${d}일 (${weekday})`;
};

/** 약 5시간 전 / 3일 전 */
export const formatRelative = (value) => {
  const date = toDate(value);
  if (!date) return '';

  const diffMs = Date.now() - date.getTime();
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return '방금';
  if (minutes < 60) return `${minutes}분 전`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `약 ${hours}시간 전`;

  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}일 전`;

  const months = Math.floor(days / 30);
  if (months < 12) return `${months}개월 전`;
  return `${Math.floor(months / 12)}년 전`;
};

/**
 * 최상위 기록(부모 없는 것)에 자식 생각을 붙여 트리로 만든다.
 * @param {Array} records 한 책의 기록 목록
 * @param {'desc'|'asc'} order 최신순 | 오래된순
 */
export const buildRecordTree = (records, order = 'desc') => {
  const children = new Map();
  const roots = [];

  records.forEach(record => {
    if (record.parentId) {
      const bucket = children.get(record.parentId);
      if (bucket) bucket.push(record);
      else children.set(record.parentId, [record]);
    } else {
      roots.push(record);
    }
  });

  // 부모가 삭제된 고아 생각은 최상위로 끌어올려 유실을 막는다
  const rootIds = new Set(roots.map(r => r.id));
  children.forEach((bucket, parentId) => {
    if (!rootIds.has(parentId)) {
      roots.push(...bucket);
      children.delete(parentId);
    }
  });

  const dir = order === 'asc' ? 1 : -1;
  roots.sort((a, b) => (recordTimeMs(a) - recordTimeMs(b)) * dir);
  children.forEach(bucket => bucket.sort((a, b) => recordTimeMs(a) - recordTimeMs(b)));

  return roots.map(record => ({
    ...record,
    children: children.get(record.id) || []
  }));
};

/** 날짜별로 묶는다. [{ date, records, pageMark, count }] */
export const groupByDate = (tree, order = 'desc') => {
  const map = new Map();

  tree.forEach(record => {
    const key = record.date || '';
    const bucket = map.get(key);
    if (bucket) bucket.push(record);
    else map.set(key, [record]);
  });

  const dir = order === 'asc' ? 1 : -1;
  return [...map.entries()]
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0) * dir)
    .map(([date, records]) => {
      const pages = records
        .map(r => r.page)
        .filter(p => typeof p === 'number' && p > 0);

      return {
        date,
        records,
        // 그 날 기록된 페이지 중 가장 뒤쪽 = 그 날의 도달 지점
        pageMark: pages.length ? Math.max(...pages) : null,
        // 자식 생각까지 포함한 실제 기록 수
        count: records.reduce((sum, r) => sum + 1 + (r.children?.length || 0), 0)
      };
    });
};
